import { Injectable, inject } from '@angular/core';

import {
    CookieName,
    CookieService,
    DeviceService,
    NativeAppService,
    NavigationService,
    Page,
    ParsedUrl,
    UrlService,
    UserService,
    WINDOW,
} from '@frontend/vanilla/core';
import { BalancePropertiesConfig } from '@frontend/vanilla/features/balance-properties';
import { firstValueFrom } from 'rxjs';

import { TrackingConfig } from './tracking.client-config';

/**
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class TrackingValueGettersService {
    private referrer: string;
    private entryUrl: ParsedUrl;
    readonly #window = inject(WINDOW);
    private readonly trackingConfig = inject(TrackingConfig);
    private readonly userService = inject(UserService);
    private readonly navigationService = inject(NavigationService);
    private readonly urlService = inject(UrlService);
    private readonly page = inject(Page);
    private readonly cookieService = inject(CookieService);
    private readonly deviceService = inject(DeviceService);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly balancePropertiesConfig = inject(BalancePropertiesConfig);

    async userBalance(): Promise<number> {
        if (this.userService.isAuthenticated) {
            await firstValueFrom(this.balancePropertiesConfig.whenReady);
            return this.balancePropertiesConfig.balanceProperties.accountBalance;
        }
        return 0;
    }

    async hasPositiveBalance(): Promise<boolean> {
        if (this.userService.isAuthenticated) {
            await firstValueFrom(this.balancePropertiesConfig.whenReady);
            return this.balancePropertiesConfig.balanceProperties.accountBalance > 0;
        }
        return false;
    }

    locationAbsUrl(): string {
        return this.removeNotTrackedQueryStrings(this.navigationService.location.absUrl(), true);
    }

    locationPathQueryAndFragment(): string {
        return TrackingValueGettersService.removeLeadingSlashes(this.removeNotTrackedQueryStrings(this.navigationService.location.url(), false));
    }

    browserScreenResolution(): string {
        if (this.#window) {
            const { width, height } = this.deviceService.windowRect();
            return `${width}x${height}`;
        }

        return `${this.deviceService.displayWidth}x${this.deviceService.displayHeight}`;
    }

    pageName() {
        const langPrefix = `/${this.page.lang}`;
        const path = this.navigationService.location.path();
        const lowerCasePath = path.toLowerCase();
        let pageName: string;

        if (lowerCasePath.startsWith(langPrefix)) {
            const result = path.substring(langPrefix.length);
            if (result === '') {
                pageName = '/';
            } else {
                pageName = result;
            }
        } else {
            pageName = path;
        }

        pageName = TrackingValueGettersService.removeLeadingSlashes(pageName);

        return pageName;
    }

    pageReferrer(): string {
        return this.removeNotTrackedQueryStrings(this.referrer, true);
    }

    nightMode(): string {
        return `Night Mode ${parseInt(this.cookieService.get(CookieName.DarkMode)) > 0 ? 'ON' : 'OFF'}`;
    }

    medium(): string {
        if (this.nativeAppService.nativeMode === 'Unknown') {
            if (this.deviceService.isTablet) return 'tablet web';
            else if (this.deviceService.isMobile) return 'mobile web';
            else return 'desktop web';
        }

        let os = '';
        if (this.nativeAppService.isNativeApp || this.nativeAppService.isNativeWrapper) {
            if (this.deviceService.isiOS) os = 'ios';
            if (this.deviceService.isChrome) os = 'android';
        }

        return `${this.nativeAppService.nativeMode} ${os}`;
    }

    fullEntryUrl(): ParsedUrl {
        return this.entryUrl;
    }

    setEntryUrl(entryUrl: string) {
        this.entryUrl = this.urlService.parse(entryUrl);
    }

    setReferrer(referrer: string) {
        this.referrer = referrer;
    }

    private removeNotTrackedQueryStrings(url: string, absolute: boolean): string {
        if (!url) {
            return '';
        }

        const parsedUrl = this.urlService.parse(url);
        for (const notTrackedQueryString of this.trackingConfig.notTrackedQueryStrings || []) {
            parsedUrl.search.delete(notTrackedQueryString);
        }
        return decodeURIComponent(absolute ? parsedUrl.absUrl() : parsedUrl.url());
    }

    private static removeLeadingSlashes(input: string): string {
        return input.replace(/^\/*/, '');
    }
}
